import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {EventConstants, FluxAction} from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import Store from 'lib/Store';
import {TrayTask, TrayTaskOptions} from 'tray/TrayTaskTypes';

const TRAY_TASK_DEFAULTS: TrayTaskOptions = {
  heading: '',
  onHeadingClick: undefined,
  subheading: '',
  onSubheadingClick: undefined,
  icon: undefined,
  progress: 0, // value between 0 and 1 or one of the constants in TrayTaskConstants
  error: '',
};

export interface TrayTaskStoreState {
  tasks: Array<TrayTask>;
  isTrayOpen: boolean;
}

class TrayTaskStore extends Store {
  tasks!: Array<TrayTask>;
  isTrayOpen!: boolean;

  override resetState(): void {
    // @tasks is an array, not a dictionary, because its eventual purpose is to be ordered in a view. We still
    // key to it with an id (rather than an index) because tasks can be arbitrarily removed, and it's a lot
    // easier to keep track of them through constant ids rather than changing indices.
    this.tasks = [];
    this.isTrayOpen = false;
  }

  getState(): TrayTaskStoreState {
    return {
      tasks: this.tasks,
      isTrayOpen: this.isTrayOpen,
    };
  }

  getTask(id: string): TrayTask | undefined {
    // eslint-disable-next-line no-restricted-syntax
    return _.find(this.tasks, {id});
  }

  override payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.TRAY_TASK_ADD:
        this.addTrayTask(action.taskId, action.options);
        this.emitChange();
        break;

      case EventConstants.TRAY_TASK_UPDATE:
        this.updateTrayTask(action.taskId, action.options);
        this.emitChange();
        break;

      case EventConstants.TRAY_TASK_REMOVE:
        this.removeTrayTask(action.taskId);
        this.emitChange();
        break;

      case EventConstants.TRAY_TASK_TOGGLE_OPEN:
        this.isTrayOpen = !this.isTrayOpen;
        this.emitChange();
        break;

      case EventConstants.TRAY_TASK_SHOW:
        this.isTrayOpen = true;
        this.emitChange();
        break;

      case EventConstants.TRAY_TASK_HIDE:
        this.isTrayOpen = false;
        this.emitChange();
        break;

      default:
        break;
    }

    return true;
  }

  addTrayTask(taskId: string, options: TrayTaskOptions): void {
    if (this.getTask(taskId) != null) {
      throw new Error(`Task with id ${taskId} already exists`);
    }

    const task = _.defaults({}, options, TRAY_TASK_DEFAULTS);
    task.id = taskId;
    this.tasks.push(task);
  }

  updateTrayTask(taskId: string, options: Partial<TrayTaskOptions>): void {
    // eslint-disable-next-line no-restricted-syntax
    const index = _.findIndex(this.tasks, {id: taskId});
    if (index === -1) {
      return;
    }
    const task = this.tasks[index];

    const newTask = reactUpdate<TrayTask>(task, {$merge: options});
    this.tasks = reactUpdate(this.tasks, {
      $splice: [[index, 1, newTask]],
    });
  }

  removeTrayTask(taskId: string): void {
    // eslint-disable-next-line no-restricted-syntax
    const index = _.findIndex(this.tasks, {id: taskId});
    if (index === -1) {
      return;
    }
    this.tasks = reactUpdate(this.tasks, {
      $splice: [[index, 1]],
    });
  }
}

export default new TrayTaskStore(AppDispatcher);
